import React, { useEffect, useState } from 'react';
import { css } from 'aphrodite';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Userpilot } from 'userpilot';

import {
  useConnectStripe,
  useDisconnectStripe,
  useGetConnectedAccounts
} from '../../hooks/usePayment';
import TextBase from '../../common/text';
import { Colors } from '../../themes/Colors';
import AppLogo from '../../assets/image/svg/app-logo.svg';
import { useQueryClient } from '@tanstack/react-query';
import StripeLogo from '../../assets/image/svg/stripe.svg';
import StripeWordsLogo from '../../assets/image/svg/stripe-words.svg';
import ConvertIcon from '../../assets/image/svg/convert.svg';

import { styles } from './styles';
import LocalStorage from '../../defined/localStorage';
import { useSelector } from 'react-redux';
import { ENV } from '../../Env';
import CustomSpinner from '../../common/CustomSpinner';
import { usePermissions } from '../../hooks/usePermissions';
import Utils from '../../utils';
import { resolveCssVarValue } from '../../utils/CommonFunction';
import AnimatedDots from '../../common/animated-dots';
import dayjs from 'dayjs';
import { SHOW_MONTH_NAME_FORMAT } from '../../modules/order-detail/helper';
import PageTitleHeader from '../../common/page-title-header';
import { translate } from '../../helper';

const brand = JSON.parse(LocalStorage.getItem('branding'));

function ConnectStripe() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const whiteLabel = useSelector((state) => state.User?.branding);
  const { canEdit } = usePermissions();
  const canEditPaymentProcessing = canEdit('payment_processing');

  const { connectedAccount, isLoadingConnectedAccount, refetch, isFetched } =
    useGetConnectedAccounts(onError, queryClient);
  const { connectStripe } = useConnectStripe(onSuccessConnect, onErrorConnect);
  const { disconnectStripe } = useDisconnectStripe(onSuccessDisconnect, onErrorDisconnect);

  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code');

  const [transitionType, setTransitionType] = useState(code ? 'connect' : null);

  const handleConnectSubmit = (code) => {
    if (!code) return;

    setTransitionType('connect');

    connectStripe({ code });
  };

  React.useEffect(() => {
    document.title = 'Connect Stripe';
  }, []);

  // Clean up already_redirected param from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.has('already_redirected')) {
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    handleConnectSubmit(code);
  }, [code]);

  const handleDisconnect = () => {
    setTransitionType('disconnect');

    setIsDisconnectModalOpen(false);

    disconnectStripe();
  };

  const handleConnect = () => {
    const callback_url = `${ENV.CURRENT_DOMAIN}/`;
    const stripeUrl = `${ENV.CURRENT_DOMAIN}/stripe/accounts/connect/authorize/web/?` +
        new URLSearchParams({
            callback_url: ENV.STRIPE_CALLBACK_URL,
            state: `${callback_url}connect-stripe`
        }).toString();
    window.location.href = stripeUrl;
  };

  const showDisconnectConfirm = () => {
    setIsDisconnectModalOpen(true);
  };

  function clearSearchParams() {
    navigate({
      search: ''
    });
  }

  async function onSuccessConnect() {
    Userpilot.track('Stripe Connected');

    clearSearchParams();

    await refetch();
  }

  function onErrorConnect(error) {
    // Reset transition state on connect error so UI goes back to connect button
    setTransitionType(null);
  }

  async function onSuccessDisconnect() {
    await refetch();
  }

  function onErrorDisconnect(error) {
    // Reset transition state on disconnect error so UI goes back to account data
    setTransitionType(null);
  }

  useEffect(() => {
    // connect complete
    if (transitionType === 'connect' && connectedAccount?.id) {
      setTransitionType(null);
    }

    // disconnect complete
    if (transitionType === 'disconnect' && !connectedAccount?.id) {
      setTransitionType(null);
    }
  }, [connectedAccount, transitionType]);
  function onError(error) {
    // setConnected({});
  }

  const maskEmail = (email) => {
    if (!email) return '-';
    const [name, domain] = email.split('@');
    return name.slice(0, 3) + '***@' + domain;
  };

  const formatDate = (date) => {
    if (!date) return '-';

    return dayjs(date).format(SHOW_MONTH_NAME_FORMAT);
  };

  // Make Stripe requirements readable
  const formatRequirement = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\./g, ' → ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const stripeAccount = connectedAccount?.account || {};
  const accountEmail = stripeAccount?.email || stripeAccount?.business_profile?.support_email;

  const requirements = stripeAccount?.requirements || {};
  const accountMeta = [
    { label: 'Connected as', value: maskEmail(accountEmail) },
    { label: 'Account ID', value: stripeAccount?.id || '-' },
    { label: 'Country', value: stripeAccount?.country || '-' },
    { label: 'Currency', value: (stripeAccount?.default_currency || '-').toUpperCase() },
    { label: 'Type', value: stripeAccount?.type || '-' },
    { label: 'Connected On', value: formatDate(connectedAccount?.created_at) },
    { label: 'Display Name', value: connectedAccount?.display_name || '-' }
  ];
  const statusChips = [
    { label: 'Charges', value: stripeAccount?.charges_enabled, activeColor: '#16A34A' },
    { label: 'Payouts', value: stripeAccount?.payouts_enabled, activeColor: '#0EA5E9' },
    { label: 'Details Submitted', value: stripeAccount?.details_submitted, activeColor: '#7C3AED' },
    {
      label: 'Mode',
      value: stripeAccount?.livemode ? 'Live' : 'Test',
      activeColor: stripeAccount?.livemode ? '#DC2626' : '#0F766E'
    }
  ];
  const hasPendingRequirements = (requirements?.eventually_due || []).length > 0;
  const connectButtonColor = whiteLabel?.web_button_color || brand?.web_button_color || '#635BFF';
  const isConnected = !!connectedAccount?.id;

  const isInitialLoading = connectedAccount === undefined && transitionType === null && !code;

  const LoaderCard = ({ text }) => (
    <div
      style={{
        width: '100%',
        maxWidth: 'calc(var(--fs-hero) * 14)',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'calc(var(--sp-lg) * 1.2)',
        minHeight: 'calc(var(--fs-hero) * 7.075471)'
      }}>
      <CustomSpinner size={resolveCssVarValue('--fs-xl') * 2.5} />

      <TextBase
        style={{ textAlign: 'center' }}
        size={'calc(var(--fs-md2) * 1.02)'}
        fontWeight={500}
        color={'rgba(71, 85, 105, 1)'}>
        {text}
        <AnimatedDots />
      </TextBase>
    </div>
  );
  return (
    <div
      className={css(styles.container)}
      style={{
        background:
          'radial-gradient(circle at 10% 20%, rgba(99, 91, 255, 0.08) 0%, rgba(255,255,255,1) 42%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
      }}>
      <PageTitleHeader
        title={translate('acceptPayments')}
        containerStyle={{ marginBottom: 'var(--sp-lg)' }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          flex: 1
        }}>
        <div style={{ maxWidth: 'calc(var(--fs-hero)*16)', width: '100%' }}>
          <div
            style={{
              background:
                'linear-gradient(160deg, rgba(255,255,255,1) 0%, rgba(247,250,255,1) 100%)',
              minHeight: 'calc(var(--fs-hero)*4.6698)',
              display: 'flex',
              justifyContent: 'center',
              minWidth: 'calc(var(--fs-hero)*7.075)',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 'calc(var(--sp-xxs)*2)',
              border: '1px solid rgba(226, 232, 240, 1)',
              boxShadow: '0 18px 42px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.75)',
              padding: 'calc(var(--sp-lg) * 1.2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
            <div
              style={{
                position: 'absolute',
                top: 'calc(var(--fs-hero)*-2.830188)',
                right: 'calc(var(--fs-hero)*-2.830188)',
                width: 'calc(var(--fs-hero)*6.132075)',
                height: 'calc(var(--fs-hero)*6.132075)',
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(99, 91, 255, 0.16) 0%, rgba(99, 91, 255, 0) 72%)',
                pointerEvents: 'none'
              }}
            />
            {isInitialLoading ? (
              // INITIAL LOADER
              <LoaderCard text="Checking your Stripe connection" />
            ) : transitionType === 'connect' ? (
              // CONNECTING
              <LoaderCard text="Connecting to Stripe" />
            ) : transitionType === 'disconnect' ? (
              // DISCONNECTING
              <LoaderCard text="Disconnecting from Stripe" />
            ) : isConnected ? (
              // CONNECTED UI
              <div
                style={{
                  width: '100%',
                  maxWidth: 'calc(var(--fs-hero) * 14)',
                  position: 'relative',
                  zIndex: 1
                }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 'calc(var(--sp-md2) * 0.8)',
                    marginBottom: 'calc(var(--sp-lg)*1.1)'
                  }}>
                  <img
                    src={StripeWordsLogo}
                    style={{
                      height: 'calc(var(--fs-xl)*2.35849)',
                      width: 'calc(var(--fs-hero)*3.01886)'
                    }}
                  />
                </div>
                <div style={{ marginBottom: 'calc(var(--sp-md2) * 1.2)' }}>
                  <TextBase
                    style={{ textAlign: 'center' }}
                    size={'calc(var(--fs-xl)*1.132)'}
                    fontWeight={600}
                    color={Colors.success}>
                    stripeIsSuccessfullyConnected
                  </TextBase>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 'calc(var(--sp-xxs) * 2)',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    marginBottom: 'calc(var(--sp-lg) * 1.1)'
                  }}>
                  {statusChips?.map((chip, index) => {
                    const isActive = typeof chip.value === 'string' ? true : !!chip.value;
                    const valueLabel =
                      typeof chip.value === 'string'
                        ? chip.value
                        : chip.value
                        ? 'Enabled'
                        : 'Disabled';
                    return (
                      <div
                        key={index}
                        style={{
                          padding: 'calc(var(--sp-xxs) * 1.1) calc(var(--sp-md2) * 1.05)',
                          borderRadius: '999px',
                          border: `1px solid ${
                            isActive ? `${chip.activeColor}66` : 'rgba(148, 163, 184, 0.6)'
                          }`,
                          background: isActive
                            ? `${chip.activeColor}14`
                            : 'rgba(241, 245, 249, 0.9)',
                          fontSize: 'calc(var(--fs-xs) * 1.132075)',
                          color: isActive ? chip.activeColor : 'rgba(71, 85, 105, 1)',
                          fontWeight: 600,
                          lineHeight: 1
                        }}>
                        {`${chip.label}: ${valueLabel}`}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    border: '1px solid rgba(226, 232, 240, 1)',
                    borderRadius: 'calc(var(--sp-xxs) * 1.8)',
                    background: 'rgba(255, 255, 255, 0.74)',
                    padding: 'calc(var(--sp-md2) * 1.15)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 'calc(var(--sp-md2) * 0.9)',
                    marginBottom: 'calc(var(--sp-lg) * 1.1)'
                  }}>
                  {accountMeta?.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'calc(var(--sp-xxs) * 0.7)'
                      }}>
                      <TextBase
                        size={'calc(var(--fs-xs) * 1.32075)'}
                        fontWeight={500}
                        color={'rgba(100, 116, 139, 1)'}>
                        {item.label}
                      </TextBase>
                      <TextBase
                        size={'calc(var(--fs-xs) * 1.2264150)'}
                        fontWeight={600}
                        color={'rgba(15, 23, 42, 1)'}>
                        {item.value}
                      </TextBase>
                    </div>
                  ))}
                </div>

                {hasPendingRequirements && (
                  <div
                    style={{
                      border: '1px solid rgba(245, 158, 11, 0.35)',
                      background: 'rgba(245, 158, 11, 0.08)',
                      borderRadius: 'calc(var(--sp-xxs) * 1.6)',
                      padding: 'calc(var(--sp-md2) * 0.8) calc(var(--sp-md2) * 1.05)',
                      marginBottom: 'calc(var(--sp-md2) * 1.1)'
                    }}>
                    <TextBase
                      size={'calc(var(--fs-xs) * 1.32075)'}
                      fontWeight={600}
                      color={'rgba(180, 83, 9, 1)'}>
                      Additional verification required
                    </TextBase>
                    <ul
                      style={{
                        marginTop: 'calc(var(--sp-xxs) * 2)',
                        paddingLeft: 'calc(var(--sp-md2) * 1.50943)',
                        fontSize: 'calc(var(--fs-xs) * 1.32075)'
                      }}>
                      {requirements?.eventually_due?.map((req, i) => (
                        <li key={i}>{formatRequirement(req)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <Button
                  onClick={showDisconnectConfirm}
                  disabled={!canEditPaymentProcessing || transitionType !== null}
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
                    height: 'calc(var(--fs-xl)*2.35849)',
                    minWidth: 'calc(var(--fs-hero)*11.2)',
                    borderColor: Colors.stripe,
                    borderRadius: 'calc(var(--sp-xxs)*1.3)',
                    width: '100%',
                    margin: 0,
                    marginBottom: 'calc(var(--sp-md2)*0.4)',
                    boxShadow: '0 8px 24px rgba(99, 91, 255, 0.12)'
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'calc(var(--sp-xxs) * 2)',
                      justifyContent: 'center'
                    }}>
                    <TextBase
                      style={{ letterSpacing: '0.01em' }}
                      fontWeight={600}
                      size={'var(--fs-md2)'}
                      color={Colors.stripe}>
                      disconnectStripeAccount
                    </TextBase>
                  </div>
                </Button>
              </div>
            ) : (
              // Show connect state
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1
                }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--sp-lg)',
                    borderRadius: 'calc(var(--sp-lg)*2.358)',
                    width: 'calc(var(--sp-lg)*7.0754)',
                    background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2FF 100%)',
                    padding: 'calc(var(--sp-xxs) * 1.3) calc(var(--sp-md2) * 0.7)',
                    border: '1px solid rgba(226, 232, 240, 0.9)'
                  }}>
                  <img
                    src={AppLogo}
                    style={{ height: 'calc(var(--fs-body)*2)', width: 'calc(var(--fs-body)*2)' }}
                  />
                  <img
                    src={ConvertIcon}
                    style={{
                      height: 'calc(var(--fs-xs)*1.88679)',
                      width: 'calc(var(--fs-xs)*1.88679)'
                    }}
                  />
                  <img
                    src={StripeLogo}
                    style={{ height: 'calc(var(--fs-body)*2)', width: 'calc(var(--fs-body)*2)' }}
                  />
                </div>

                <TextBase
                  style={{ textAlign: 'center', marginBottom: 'calc(var(--sp-md2) * 1.05)' }}
                  size={'calc(var(--fs-md2) * 1.02)'}
                  fontWeight={500}
                  color={'rgba(71, 85, 105, 1)'}>
                  Connect Stripe to start accepting location-level payments securely.
                </TextBase>

                <Button
                  type="primary"
                  disabled={!canEditPaymentProcessing || transitionType !== null}
                  onClick={handleConnect}
                  style={{
                    background: `linear-gradient(135deg, ${connectButtonColor} 0%, #4338CA 100%)`,
                    height: 'calc(var(--fs-xl)*2.358)',
                    minWidth: 'calc(var(--fs-hero)*6.2)',
                    borderRadius: 'calc(var(--sp-xxs)*2)',
                    width: '100%',
                    margin: 'var(--sp-md2)',
                    border: 'none',
                    boxShadow: '0 14px 30px rgba(67, 56, 202, 0.28)'
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'calc(var(--sp-xxs) * 2)',
                      justifyContent: 'center'
                    }}>
                    <TextBase
                      style={{ letterSpacing: '0.015em' }}
                      fontWeight={600}
                      size={'var(--fs-md2)'}
                      color={Colors.white}>
                      connectStripe
                    </TextBase>
                  </div>
                </Button>
              </div>
            )}
          </div>

          {!isConnected && transitionType === null && !isInitialLoading && (
            <TextBase
              size={'var(--fs-md)'}
              fontWeight={400}
              color={Colors.black}
              style={{
                textAlign: 'center',
                marginTop: 'calc(var(--sp-lg) * 2)',
                color: 'rgba(71, 85, 105, 1)'
              }}>
              striprInterfaceStatement{' '}
            </TextBase>
          )}
        </div>
      </div>

      <Modal
        open={isDisconnectModalOpen}
        footer={null}
        centered
        closeIcon={null}
        width="auto"
        style={{
          maxWidth: '90vw'
        }}
        onCancel={() => setIsDisconnectModalOpen(false)}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: 'calc(var(--sp-lg)*2) calc(var(--sp-lg)*1.5)'
          }}>
          <ExclamationCircleFilled
            style={{
              fontSize: 'calc(var(--fs-xl)*2)',
              color: '#faad14',
              marginBottom: 'calc(var(--sp-lg)*1.2)'
            }}
          />
          <div
            style={{
              fontSize: 'calc(var(--fs-xl)*1.132075)',
              fontWeight: 700,
              lineHeight: 1.4,
              marginBottom: 'calc(var(--sp-lg)*1.5)',
              color: '#0f172a'
            }}>
            Are you sure you want to disconnect Stripe?
          </div>
          <div
            style={{
              display: 'flex',
              gap: 'calc(var(--sp-lg)*1)',
              justifyContent: 'center',
              width: '100%'
            }}>
            <Button
              onClick={() => setIsDisconnectModalOpen(false)}
              style={{
                fontSize: 'calc(var(--fs-xs)*1.50943)',
                padding: 'calc(var(--sp-md2)*0.7) calc(var(--sp-lg)*1.5)',
                height: 'calc(var(--fs-xl)*2)',
                borderRadius: 'var(--sp-sm)',
                fontWeight: 600
              }}>
              No
            </Button>
            <Button
              type="primary"
              danger
              disabled={!canEditPaymentProcessing || transitionType !== null}
              onClick={handleDisconnect}
              style={{
                fontSize: 'calc(var(--fs-xs)*1.50943)',
                padding: 'calc(var(--sp-md2)*0.7) calc(var(--sp-lg)*1.5)',
                height: 'calc(var(--fs-xl)*2)',
                borderRadius: 'var(--sp-sm)',
                fontWeight: 600
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'calc(var(--sp-xxs) * 2)',
                  justifyContent: 'center'
                }}>
                <span>Yes</span>
              </div>
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ConnectStripe;
